import { useCallback, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";

export type FileFormData = {
  canEdit: number | string;
  canView: number | string;
  description: string;
};

/**
 * Manages file detail form state and submission
 */
export const useFileDetailForm = (file: FormattedFile | null, onClose: () => void) => {
  const initialValues = useMemo(
    () => ({
      canEdit: file?.canEditGrant ?? "",
      canView: file?.canViewGrant ?? "",
      description: file?.description ?? ""
    }),
    [file]
  );

  const form = useForm<FileFormData>({
    defaultValues: initialValues
  });

  // Reset form when file changes to ensure values are synced
  useEffect(() => {
    if (file) {
      form.reset({
        canEdit: file.canEditGrant ?? "",
        canView: file.canViewGrant ?? "",
        description: file.description ?? ""
      });
    }
  }, [file, form]);

  const canEditFile = Boolean(file?.canEdit);

  const normalizeGrant = useCallback((value: number | string) => {
    if (value === "" || value === null || value === undefined) return null;
    return Number(value);
  }, []);

  const handleSubmit = useCallback(
    (data: FileFormData) => {
      if (!file || !file.record) return;

      const payload = {
        canEdit: normalizeGrant(data.canEdit),
        canView: normalizeGrant(data.canView),
        description: data.description?.trim() ? data.description.trim() : null
      };

      file.record.set(payload);
      onClose();
    },
    [file, normalizeGrant, onClose]
  );

  const handleFileDownload = useCallback(() => {
    if (!file) return;
    const link = document.createElement("a");
    link.href = file.originalImageUrl || "";
    link.download = file.name || "download";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [file]);

  return {
    form,
    canEditFile,
    handleSubmit,
    handleFileDownload
  };
};
